import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PolicyApi } from '@Apis';
import { useGlobalSettingStore } from 'src/models/useGlobalSettingModel';

import {
  getAtpPolicyConfig,
  getExchangeTemplateConfig,
} from '../../TemplateConfig/cfg.exchange';
import { PolicyModeEnum, PolicyTypeEnum } from '../../utils/constants';
import ExchangeOnlinePolicy from '../PolicyTemplate/Email/ExchangeOnline';
import ExchangeOnlineInbound from '@Policy/AdvancedThreatProtection/PolicyTemplate/Email/ExchangeOnlineInbound';
import ExchangeOnlineOutbound from '@Policy/AdvancedThreatProtection/PolicyTemplate/Email/ExchangeOnlineOutbound';
import GmailAtpPolicy from '@Policy/AdvancedThreatProtection/PolicyTemplate/Email/Gmail';
import GmailInlineInbound from '@Policy/AdvancedThreatProtection/PolicyTemplate/Email/GmailInlineInbound';
import GmailInlineOutbound from '@Policy/AdvancedThreatProtection/PolicyTemplate/Email/GmailInlineOutbound';
import Teams from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/Teams';
import TeamsChat from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/TeamsChat';
import OneDrive from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/OneDrive';
import SharePoint from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/SharePoint';
import Box from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/Box';
import Dropbox from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/Dropbox';
import GoogleDrive from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/GoogleDrive';
import SalesforceSandbox from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/SalesforceSandbox';
import SalesforceProduction from '@Policy/AdvancedThreatProtection/PolicyTemplate/Collaboration/SalesforceProduction';

import { useOrgStore } from 'src/models/useOrgModel';
import { useLoginUserStore } from 'src/models/useLoginUserModel';
import merge from 'lodash/merge';
import { PolicyType, PolicyType2Service } from '../../../../types/app';
import { PolicySubTypeEnum } from '../../../../types';
import { getExchangeInboundTemplateConfig } from '../../TemplateConfig/cfg.exchangeInbound';
import { getExchangeOutboundTemplateConfig } from '../../TemplateConfig/cfg.exchangeOutbound';
import { getGmailTemplateConfig } from '../../TemplateConfig/cfg.gmail';
import { getOneDriveTemplateConfig } from '../../TemplateConfig/cfg.oneDrive';
import { getSharePointTemplateConfig } from '../../TemplateConfig/cfg.sharePoint';
import { getGmailInboundTemplateConfig } from '../../TemplateConfig/cfg.gmailInbound';
import { getGmailOutboundTemplateConfig } from '../../TemplateConfig/cfg.gmailOutbound';
import { getTeamsTemplateConfig } from '../../TemplateConfig/cfg.teams';
import { getTeamsChatTemplateConfig } from '../../TemplateConfig/cfg.teamsChat';
import { getBoxTemplateConfig } from '@Policy/TemplateConfig/cfg.box';
import { getGoogleDriveTemplateConfig } from '@Policy/TemplateConfig/cfg.googledrive';
import { getDropboxTemplateConfig } from '@Policy/TemplateConfig/cfg.dropbox';
import { getSalesforceSandboxTemplateConfig } from '@Policy/TemplateConfig/cfg.salesforceSandbox';
import { getSalesforceProductionTemplateConfig } from '@Policy/TemplateConfig/cfg.salesforceProduction';

import { useBreadCrumbStore } from '@Models/useBreadCrumbModel';
import { isInUIC } from '@Utils/common';
import CSpinner from '@Components/CSpinner';
import {
  ActionEnum,
  MergedRiskAiFilterRule,
  RiskAiFilter,
} from '@Policy/utils/types';
import { replaceProductionNameInPolicyEntity } from '../../utils/fns';
import PageNotFound from '@Components/Common/NoPermission';

const Policies = () => {
  const { policyUuid } = useParams();
  const { t } = useTranslation();
  const loginUserModel = useLoginUserStore();
  const globalSettingModule = useGlobalSettingStore();
  const { orgId, switchOrgId } = useOrgStore();
  const { selectedOrgGlobalSetting } = globalSettingModule;
  const [isLoading, setIsLoading] = useState(false);
  const [policyData, setPolicyData] = useState<any>(null);
  const breadCrumbModal = useBreadCrumbStore();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        let policy = (await PolicyApi.getPolicyById(policyUuid as string)).data;
        const { companyId } = policy;

        if (orgId !== companyId) {
          switchOrgId(companyId);
          policy = (await PolicyApi.getPolicyById(policyUuid as string)).data;
        }

        if (policy.tmFileblockings) {
          if (policy.tmFileblockings.fbBoxColFolderRule) {
            delete policy.tmFileblockings.fbBoxColFolderRule;
          }
          if (policy.tmFileblockings.fbBoxNonColFolderRule) {
            delete policy.tmFileblockings.fbBoxNonColFolderRule;
          }
        }

        policy = replaceProductionNameInPolicyEntity(policy);
        setPolicyData(policy);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [orgId, policyUuid, switchOrgId]);

  const updateBreadcrumb = useCallback(
    (policyType: PolicyType, policySubType: PolicySubTypeEnum) => {
      let serviceName = '';
      if (PolicyType.ExchangeInline === policyType) {
        serviceName =
          policySubType === PolicySubTypeEnum.inbound
            ? t('common.sideTitleService.exchangeInlineInbound')
            : t('common.sideTitleService.exchangeInlineOutbound');
      } else if (PolicyType.GmailInline === policyType) {
        serviceName =
          policySubType === PolicySubTypeEnum.inbound
            ? t('common.sideTitleService.gmailInlineInbound')
            : t('common.sideTitleService.gmailInlineOutbound');
      } else {
        serviceName = t(
          `common.service.${PolicyType2Service[policyType as PolicyType]}`
        );
      }

      const breadcrumbs = isInUIC()
        ? [
            {
              breadCrumb: t('common.nativeApp.productName'),
              router: '',
            },
            {
              breadCrumb: t('side.group.policy'),
              router: '',
            },
            {
              breadCrumb: t('side.label.addATPPolicy', {
                service: serviceName,
              }),
              router: '',
            },
          ]
        : [
            {
              breadCrumb: t('side.group.policy'),
              router: '',
            },
            {
              breadCrumb: t('side.label.addATPPolicy', {
                service: serviceName,
              }),
              router: '',
            },
          ];

      breadCrumbModal.updateBreadCrumb(breadcrumbs);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t]
  );

  const policyComponent = useMemo(() => {
    if (!policyData) return null;

    let configData: any = {};
    let defaultTemplate: any = {};
    let templateData: any = {};

    const { policyType, policySubType, companyId } = policyData;
    const isMonitor = [
      PolicyModeEnum.DefaultMonitorPolicy,
      PolicyModeEnum.DefaultXDRMonitorPolicy,
    ].includes(policyData.policyMode);

    updateBreadcrumb(policyType, policySubType);

    switch (policyType) {
      case PolicyTypeEnum.Exchange:
        defaultTemplate = getExchangeTemplateConfig(
          t,
          selectedOrgGlobalSetting,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        handleRiskAiFilter(policyData, isMonitor);
        templateData = merge(defaultTemplate, policyData);
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }

        return (
          <ExchangeOnlinePolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.ExchangeInline:
        if (policySubType === PolicySubTypeEnum.inbound) {
          defaultTemplate = getExchangeInboundTemplateConfig(
            t,
            selectedOrgGlobalSetting,
            loginUserModel.user?.accountEmail
          );
          configData = getAtpPolicyConfig(
            t,
            selectedOrgGlobalSetting,
            defaultTemplate
          );
          handleRiskAiFilter(policyData, isMonitor);
          templateData = merge(defaultTemplate, policyData);
          // remove policyTarget.empty, this property is only for addPolicy frontend
          if (templateData.policyTarget?.empty) {
            templateData.policyTarget.empty = undefined;
          }
          console.log(templateData);
          return (
            <ExchangeOnlineInbound
              template={templateData}
              config={configData}
              orgId={companyId}
            />
          );
        } else if (policySubType === PolicySubTypeEnum.outbound) {
          defaultTemplate = getExchangeOutboundTemplateConfig(
            t,
            loginUserModel.user?.accountEmail
          );
          configData = getAtpPolicyConfig(
            t,
            selectedOrgGlobalSetting,
            defaultTemplate
          );
          templateData = merge(defaultTemplate, policyData);
          // remove policyTarget.empty, this property is only for addPolicy frontend
          if (templateData.policyTarget?.empty) {
            templateData.policyTarget.empty = undefined;
          }
          return (
            <ExchangeOnlineOutbound
              template={templateData}
              config={configData}
              orgId={companyId}
            />
          );
        }
        break;
      case PolicyTypeEnum.Gmail:
        defaultTemplate = getGmailTemplateConfig(
          t,
          selectedOrgGlobalSetting,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        handleRiskAiFilter(policyData, isMonitor);
        templateData = merge(defaultTemplate, policyData);
        // remove policyTarget.empty, this property is only for addPolicy frontend
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <GmailAtpPolicy
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyTypeEnum.GmailInline:
        if (policySubType === PolicySubTypeEnum.inbound) {
          defaultTemplate = getGmailInboundTemplateConfig(
            t,
            selectedOrgGlobalSetting,
            loginUserModel.user?.accountEmail
          );
          configData = getAtpPolicyConfig(
            t,
            selectedOrgGlobalSetting,
            defaultTemplate
          );
          handleRiskAiFilter(policyData, isMonitor);
          templateData = merge(defaultTemplate, policyData);
          // remove policyTarget.empty, this property is only for addPolicy frontend
          if (templateData.policyTarget?.empty) {
            templateData.policyTarget.empty = undefined;
          }
          return (
            <GmailInlineInbound
              template={templateData}
              config={configData}
              orgId={companyId}
            />
          );
        } else if (policySubType === PolicySubTypeEnum.outbound) {
          defaultTemplate = getGmailOutboundTemplateConfig(
            t,
            loginUserModel.user?.accountEmail
          );
          configData = getAtpPolicyConfig(
            t,
            selectedOrgGlobalSetting,
            defaultTemplate
          );
          templateData = merge(defaultTemplate, policyData);
          // remove policyTarget.empty, this property is only for addPolicy frontend
          if (templateData.policyTarget?.empty) {
            templateData.policyTarget.empty = undefined;
          }
          return (
            <GmailInlineOutbound
              template={templateData}
              config={configData}
              orgId={companyId}
            />
          );
        }
        break;
      case PolicyType.OneDrive:
        defaultTemplate = getOneDriveTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        templateData = merge(defaultTemplate, policyData);
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <OneDrive
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.SharePoint:
        defaultTemplate = getSharePointTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        templateData = merge(defaultTemplate, policyData);
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <SharePoint
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.Teams:
        defaultTemplate = getTeamsTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        templateData = merge(defaultTemplate, policyData);
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <Teams
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.TeamsChat:
        defaultTemplate = getTeamsChatTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        templateData = merge(defaultTemplate, policyData);
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <TeamsChat
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.Box:
        defaultTemplate = getBoxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        templateData = merge(defaultTemplate, policyData);
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <Box template={templateData} config={configData} orgId={companyId} />
        );
      case PolicyType.Dropbox:
        defaultTemplate = getDropboxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        templateData = merge(defaultTemplate, policyData);
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <Dropbox
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.GoogleDrive:
        defaultTemplate = getGoogleDriveTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        templateData = merge(defaultTemplate, policyData);
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <GoogleDrive
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.SalesforceSandbox:
        defaultTemplate = getSalesforceSandboxTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        templateData = merge(defaultTemplate, policyData);
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <SalesforceSandbox
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      case PolicyType.SalesforceProduction:
        defaultTemplate = getSalesforceProductionTemplateConfig(
          t,
          loginUserModel.user?.accountEmail
        );
        configData = getAtpPolicyConfig(
          t,
          selectedOrgGlobalSetting,
          defaultTemplate
        );
        templateData = merge(defaultTemplate, policyData);
        if (templateData.policyTarget?.empty) {
          templateData.policyTarget.empty = undefined;
        }
        return (
          <SalesforceProduction
            template={templateData}
            config={configData}
            orgId={companyId}
          />
        );
      default:
        return <PageNotFound />;
    }
  }, [
    loginUserModel.user?.accountEmail,
    policyData,
    selectedOrgGlobalSetting,
    t,
    updateBreadcrumb,
  ]);

  if (isLoading) return <CSpinner />;
  if (!policyData) return <div>No policy data available</div>;

  return <>{policyComponent}</>;
};

function riskAiSetPassActions(policy: any) {
  policy.tmRiskAiFilters = {
    ...policy.tmRiskAiFilters,
    raiActionConfirmed: ActionEnum.pass,
    raiActionSpam: ActionEnum.pass,
    raiSuperAggActionSuspicious: ActionEnum.pass,
    raiSuperAggActionUnwanted: ActionEnum.pass,
    raiAggActionSuspicious: ActionEnum.pass,
    raiAggActionUnwanted: ActionEnum.pass,
    raiConsActionSuspicious: ActionEnum.pass,
    raiConsActionUnwanted: ActionEnum.pass,
  } satisfies { [K in keyof RiskAiFilter]?: RiskAiFilter[K] };
}

function riskAiSetPassRulesActions(policy: any) {
  policy.tmRiskAiFilters.tmRaiFilterRules =
    policy.tmRiskAiFilters.tmRaiFilterRules?.map(
      (rule: any) =>
        ({
          ...rule,
          raiRuleAction: ActionEnum.pass,
        }) satisfies MergedRiskAiFilterRule
    );
}

function handleRiskAiFilter(policy: any, isMonitor: boolean) {
  const isGmail = [PolicyTypeEnum.Gmail, PolicyTypeEnum.GmailInline].includes(
    policy.policyType
  );
  if (!policy.tmRiskAiFilters) {
    // Set actions to pass by default for monitor policies
    if (isMonitor) {
      policy.tmRiskAiFilters = {
        raiEnabled: 0,
      } satisfies { [K in keyof RiskAiFilter]?: RiskAiFilter[K] };
      riskAiSetPassActions(policy);
    } else {
      policy.tmRiskAiFilters = {
        raiEnabled: 0,
      } satisfies { [K in keyof RiskAiFilter]?: RiskAiFilter[K] };
    }
  } else if (isMonitor) {
    riskAiSetPassActions(policy);
    riskAiSetPassRulesActions(policy);
  } else if (isGmail && policy.tmRiskAiFilters.raiActionSpam === undefined) {
    // For existing Gmail policies without raiActionSpam field,
    // default to moveToSpam to match scanner behavior
    policy.tmRiskAiFilters.raiActionSpam = ActionEnum.moveToSpam;
  }
}

export default Policies;
